import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { m } from "framer-motion";
// @mui
import { Badge, Box, Divider, IconButton, List, Stack, Typography } from "@mui/material";
// routes
import { paths } from "src/routes/paths";
import { useRouter } from "src/routes/hook";
// hook
import usePopover from "src/hooks/use-popover";
// redux
import { batch, useDispatch } from "src/redux/store";
import { updateDisplayTaskPopover } from "src/redux/slices/other";
import { checkTaskById, getTaskPopoverList, getTaskUnFinishedCountSuccess, getUnreadTaskNum } from "src/redux/slices/task";
// components
import Image from "src/components/image";
import Scrollbar from "src/components/scrollbar";
import { varHover } from "src/components/animate";
import Button from "src/components/template/button";
import Popover from "src/components/template/popover";
// sections
import TaskErrorDialog from "src/sections/task/dialog/task-error-dialog";
//
import TaskItem from "./task-item";

// ----------------------------------------------------------------------

let refreshTask = null;

// ----------------------------------------------------------------------

export default function TaskPopover() {
    const popover = usePopover();

    const router = useRouter();

    const dispatch = useDispatch();

    const buttonRef = useRef();

    const { showTaskPopover } = useSelector((state) => state.other);
    const { tasksPopover, totalUnFinished } = useSelector((state) => state.task);

    const [ tableData, setTableData ] = useState([]);
    //
    const [ dialog, setDialog ] = useState(null);

    const handleGoToTask = () => {
        dispatch(updateDisplayTaskPopover(false));
        //
        router.push(`${ paths.dashboard.tasks }`);
    };

    const handleDirectOpen = (event) => batch(() => {
        popover.onOpen(event);
        //
        dispatch(getUnreadTaskNum());
        dispatch(getTaskPopoverList()).then(() => dispatch(updateDisplayTaskPopover(true)));
    });

    const handleOnOpen = () => buttonRef.current?.click();
    const handleOnClose = () => dispatch(updateDisplayTaskPopover(false));

    const handleOnErrorDialog = (errors) => {
        setDialog(
            <TaskErrorDialog
                open
                onClose={ () => setDialog(null) }
                detail={ errors }
            />
        );
    };

    useEffect(() => {
        if (showTaskPopover && !popover.open) {
            handleOnOpen();
        }

        if (!showTaskPopover && popover.open) {
            popover.onClose();
        }
    }, [ showTaskPopover, popover ]);

    useEffect(() => setTableData(tasksPopover), [ tasksPopover ]);

    useEffect(() => {
        if (popover.open) {
            const ids = tableData.filter(t => t.status === "PROCESSING").map(t => t.id);

            if (ids.length > 0) {
                dispatch(getTaskUnFinishedCountSuccess(1));

                refreshTask = setTimeout(() => {
                    dispatch(checkTaskById(ids)).then(result => {
                        const localTableData = [ ...tableData ];

                        const updatedLocalTableData = localTableData.map(data => {
                            if (result[ data.id ]) {
                                return result[ data.id ];
                            }
                            return data;
                        });

                        setTableData(updatedLocalTableData);
                    });
                }, 2500);
            } else {
                dispatch(getTaskUnFinishedCountSuccess(0));
            }
        }

        return () => {
            clearTimeout(refreshTask);
        };
    }, [ popover, tableData ]);

    const renderHead = (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={ { py: "19px", px: "15px" } }>
            <Typography variant="h3" color="text.primary" sx={ { flexGrow: 1 } }>
                Task Manager
            </Typography>
        </Stack>
    );

    const renderList = (
        <List disablePadding>
            { tableData.map((task, index) => (
                <Stack key={ index } sx={ { paddingX: "20px" } }>
                    { index !== 0 && (
                        <Divider sx={ { borderStyle: "dashed", borderColor: "line" } } />
                    ) }

                    <TaskItem detail={ task } onDialog={ handleOnErrorDialog } />
                </Stack>
            )) }
        </List>
    );

    const renderFoot = (
        <Box sx={ { width: 1, height: "50px" } }>
            <Button variant="outlined" layout="full" sx={ { height: "100%", borderColor: "transparent !important", borderRadius: 0 } } onClick={ handleGoToTask }>
                View All
            </Button>
        </Box>
    );

    return (
        <>
            <IconButton
                ref={ buttonRef }
                component={ m.button }
                whileTap="tap"
                whileHover="hover"
                variants={ varHover(1.05) }
                color={ popover.open ? "primary" : "default" }
                onClick={ handleDirectOpen }
                sx={ {
                    padding: "4px",
                    ...( popover.open && {
                        backgroundColor: (theme) => theme.palette.action.selected
                    } )
                } }
            >
                <Badge variant="dot" color="error" sx={ { "& .MuiBadge-dot": { top: "4px", right: "4px" } } } invisible={ !totalUnFinished }>
                    <Image src="/assets/icon/navbar/ic_task.svg" alt="task" objectFit="contain" sx={ { width: "24px", height: "24px" } } disabledEffect visibleByDefault />
                </Badge>
            </IconButton>

            <Popover
                open={ popover.open }
                onClose={ popover.onClose }
                onBackdropClick={ handleOnClose }
                sx={ { width: 360, marginTop: "6px", padding: 0, borderRadius: "14px", overflow: "hidden", "&.MuiPaper-root": { maxWidth: "83%" } } }
                hiddenArrow
            >
                { renderHead }

                <Divider sx={ { borderColor: "line" } } />

                <Scrollbar sx={ { height: "fit-content", maxHeight: 300 } }>
                    { renderList }
                </Scrollbar>

                <Divider sx={ { borderColor: "line" } } />

                { renderFoot }
            </Popover>

            { dialog }
        </>
    );
}
